/**
 * TerminalThemeMenu — toolbar-slot dropdown for picking the terminal's xterm theme.
 *
 * Rendered by WidgetFloatingToolbar via the preset's `toolbarSlot`. Writes the chosen
 * theme id to the node's Loro `data.theme`; TerminalWidget applies it live.
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Palette } from "lucide-react";
import { useCanvasApi } from "../../../context/CanvasContext";
import { DEFAULT_TERMINAL_THEME_ID, TERMINAL_THEMES } from "./themes";

interface Props {
  id: string;
  data: Record<string, unknown>;
}

export function TerminalThemeMenu({ id, data }: Props) {
  const canvas = useCanvasApi();
  const current = String(data.theme ?? DEFAULT_TERMINAL_THEME_ID);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          title="Terminal theme"
          aria-label="Terminal theme"
          onClick={(e) => e.stopPropagation()}
          className="h-7 w-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-black/8 dark:hover:bg-white/10 transition-colors focus:outline-none"
        >
          <Palette className="h-[15px] w-[15px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Terminal theme</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={current}
          onValueChange={(value) => canvas.updateNodeData(id, { theme: value })}
        >
          {TERMINAL_THEMES.map((t) => (
            <DropdownMenuRadioItem
              key={t.id}
              value={t.id}
              onClick={(e) => e.stopPropagation()}
            >
              {t.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
